import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../utils/currency';
import toast from 'react-hot-toast';
import {
  ArrowPathIcon,
  BanknotesIcon,
  CheckIcon,
  PrinterIcon
} from '@heroicons/react/24/outline';

const today = new Date().toISOString().split('T')[0];
const currentMonth = new Date().getMonth() + 1;
const currentYear = new Date().getFullYear();

const monthName = (month) => new Date(2000, Number(month || 1) - 1, 1).toLocaleString('en-US', { month: 'long' });
const periodLabel = (run) => (run ? `${monthName(run.periodMonth)} ${run.periodYear}` : 'Payroll');

const actorLabel = (person) => {
  if (!person?.name) return 'N/A';
  return person.employeeId ? `${person.name} (${person.employeeId})` : person.name;
};

const safeText = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => ({
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;'
}[char]));

const StatCard = ({ label, value, subtitle, tone = 'text-gray-900' }) => (
  <div className="card p-4">
    <p className="text-sm font-medium text-gray-500">{label}</p>
    <p className={`mt-1 text-2xl font-semibold ${tone}`}>{value}</p>
    {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
  </div>
);

const statusBadge = (status) => {
  switch (status) {
    case 'approved':
      return 'status-approved';
    case 'paid':
      return 'status-paid';
    case 'cancelled':
      return 'status-rejected';
    default:
      return 'status-pending';
  }
};

const lineStatusBadge = (status) => {
  if (status === 'paid') return 'status-paid';
  if (status === 'approved') return 'status-approved';
  if (status === 'held') return 'status-rejected';
  return 'status-pending';
};

const Payroll = () => {
  const { user, hasRole } = useAuth();
  const isFinance = hasRole(['admin', 'director', 'accounts']);
  const canSetupCompensation = hasRole(['admin', 'director']);
  const canPreparePayroll = hasRole(['admin', 'accounts']);
  const canApprovePayroll = hasRole(['admin', 'director']);
  const canPayPayroll = hasRole(['admin', 'accounts']);

  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [compensations, setCompensations] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [runs, setRuns] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [selectedLines, setSelectedLines] = useState([]);
  const [lineDrafts, setLineDrafts] = useState({});
  const [employeeHistory, setEmployeeHistory] = useState([]);
  const [newRun, setNewRun] = useState({ periodMonth: currentMonth, periodYear: currentYear, notes: '' });
  const [transactionForm, setTransactionForm] = useState({
    employee: '',
    balanceType: 'advance',
    direction: 'increase',
    amount: '',
    date: today,
    description: '',
    referenceNumber: ''
  });

  const loadFinanceData = useCallback(async () => {
    if (!isFinance) return;

    try {
      setLoading(true);
      const [summaryRes, compRes, txRes, runRes] = await Promise.all([
        api.get('/api/payroll/summary'),
        api.get('/api/payroll/compensations'),
        api.get('/api/payroll/balance-transactions'),
        api.get('/api/payroll/runs')
      ]);

      setSummary(summaryRes.data);
      setCompensations(compRes.data.rows || []);
      setTransactions(txRes.data.transactions || []);
      setRuns(runRes.data.runs || []);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load payroll data');
    } finally {
      setLoading(false);
    }
  }, [isFinance]);

  const loadEmployeeHistory = useCallback(async () => {
    if (isFinance) return;

    try {
      setLoading(true);
      const response = await api.get('/api/payroll/my-history');
      setEmployeeHistory(response.data.lines || []);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load payroll history');
    } finally {
      setLoading(false);
    }
  }, [isFinance]);

  useEffect(() => {
    loadFinanceData();
    loadEmployeeHistory();
  }, [loadFinanceData, loadEmployeeHistory]);

  const employees = useMemo(() => compensations.map((row) => row.employee), [compensations]);

  const openRun = async (runId) => {
    try {
      const response = await api.get(`/api/payroll/runs/${runId}`);
      setSelectedRun(response.data.run);
      setSelectedLines(response.data.lines || []);
      const drafts = {};
      (response.data.lines || []).forEach((line) => {
        drafts[line._id] = {
          baseSalary: line.baseSalary,
          bonus: line.bonus,
          advanceDeduction: line.advanceDeduction,
          loanDeduction: line.loanDeduction,
          otherDeduction: line.otherDeduction,
          status: line.status === 'held' ? 'held' : 'draft',
          notes: line.notes || ''
        };
      });
      setLineDrafts(drafts);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to open payroll run');
    }
  };

  const saveCompensation = async (row) => {
    const input = window.prompt(`Monthly salary for ${row.employee.name}`, row.baseSalary || 0);
    if (input === null) return;

    const amount = Number(input);
    if (!Number.isFinite(amount) || amount < 0) {
      toast.error('Salary must be zero or greater');
      return;
    }

    try {
      await api.put(`/api/payroll/compensations/${row.employee._id}`, {
        baseSalary: amount,
        effectiveFrom: today
      });
      toast.success('Salary setup saved');
      loadFinanceData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save salary');
    }
  };

  const createTransaction = async (event) => {
    event.preventDefault();
    try {
      await api.post('/api/payroll/balance-transactions', transactionForm);
      toast.success('Advance/loan transaction recorded');
      setTransactionForm((prev) => ({ ...prev, amount: '', description: '', referenceNumber: '' }));
      loadFinanceData();
    } catch (error) {
      const firstValidation = error.response?.data?.errors?.[0]?.msg;
      toast.error(firstValidation || error.response?.data?.message || 'Failed to record transaction');
    }
  };

  const createRun = async (event) => {
    event.preventDefault();
    try {
      const response = await api.post('/api/payroll/runs', newRun);
      toast.success('Payroll draft created');
      await loadFinanceData();
      setSelectedRun(response.data.run);
      setSelectedLines(response.data.lines || []);
    } catch (error) {
      const firstValidation = error.response?.data?.errors?.[0]?.msg;
      toast.error(firstValidation || error.response?.data?.message || 'Failed to create payroll draft');
    }
  };

  const updateLineDraft = (lineId, field, value) => {
    setLineDrafts((prev) => ({
      ...prev,
      [lineId]: {
        ...prev[lineId],
        [field]: value
      }
    }));
  };

  const saveLine = async (lineId) => {
    try {
      const payload = lineDrafts[lineId];
      const response = await api.put(`/api/payroll/runs/${selectedRun._id}/lines/${lineId}`, payload);
      setSelectedRun(response.data.run);
      setSelectedLines(response.data.lines || []);
      toast.success('Payroll line updated');
      loadFinanceData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update payroll line');
    }
  };

  const approveRun = async () => {
    if (!selectedRun || !window.confirm(`Approve payroll for ${periodLabel(selectedRun)}?`)) return;
    try {
      const response = await api.put(`/api/payroll/runs/${selectedRun._id}/approve`);
      setSelectedRun(response.data.run);
      setSelectedLines(response.data.lines || []);
      toast.success('Payroll approved');
      loadFinanceData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to approve payroll');
    }
  };

  const payRun = async () => {
    if (!selectedRun) return;
    const paymentReference = window.prompt('Cash voucher/reference number (optional)', selectedRun.paymentReference || '');
    if (paymentReference === null) return;

    try {
      const response = await api.put(`/api/payroll/runs/${selectedRun._id}/pay`, { paymentReference });
      setSelectedRun(response.data.run);
      setSelectedLines(response.data.lines || []);
      toast.success('Payroll marked paid');
      loadFinanceData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to mark payroll paid');
    }
  };

  const printPayslip = (line) => {
    const run = line.payrollRun || selectedRun || {};
    const employee = line.employee || user || {};
    const name = employee.name || 'Employee';
    const employeeId = employee.employeeId || '';
    const preparedBy = actorLabel(run.preparedBy);
    const approvedBy = actorLabel(run.approvedBy);
    const paidBy = actorLabel(run.paidBy);
    const paidAt = run.paidAt ? new Date(run.paidAt).toLocaleDateString() : 'N/A';
    const content = `
      <html>
        <head>
          <title>Payslip - ${safeText(name)}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 32px; color: #111827; }
            h1 { margin-bottom: 4px; }
            table { width: 100%; border-collapse: collapse; margin-top: 24px; }
            td { border-bottom: 1px solid #e5e7eb; padding: 10px 0; }
            .right { text-align: right; font-weight: bold; }
            .meta { color: #4b5563; margin: 4px 0; }
            .signatures { display: grid; grid-template-columns: repeat(3, 1fr); gap: 32px; margin-top: 64px; }
            .line { border-top: 1px solid #111827; padding-top: 8px; text-align: center; }
            .actor { display: block; margin-top: 4px; color: #4b5563; font-size: 12px; }
          </style>
        </head>
        <body>
          <h1>Fin Accounting Payslip</h1>
          <p class="meta"><strong>Period:</strong> ${safeText(periodLabel(run))}</p>
          <p class="meta"><strong>Employee:</strong> ${safeText(name)} ${employeeId ? `(${safeText(employeeId)})` : ''}</p>
          <p class="meta"><strong>Status:</strong> ${safeText(line.status || run.status || 'N/A')} | <strong>Paid date:</strong> ${safeText(paidAt)}</p>
          ${run.paymentReference ? `<p class="meta"><strong>Cash voucher/reference:</strong> ${safeText(run.paymentReference)}</p>` : ''}
          <table>
            <tr><td>Base Salary</td><td class="right">${formatCurrency(line.baseSalary)}</td></tr>
            <tr><td>Bonus</td><td class="right">${formatCurrency(line.bonus)}</td></tr>
            <tr><td>Advance Deduction</td><td class="right">${formatCurrency(line.advanceDeduction)}</td></tr>
            <tr><td>Loan Deduction</td><td class="right">${formatCurrency(line.loanDeduction)}</td></tr>
            <tr><td>Other Deduction</td><td class="right">${formatCurrency(line.otherDeduction)}</td></tr>
            <tr><td>Net Payable</td><td class="right">${formatCurrency(line.netPayable)}</td></tr>
          </table>
          <div class="signatures">
            <div class="line">Prepared By<span class="actor">${safeText(preparedBy)}</span></div>
            <div class="line">Approved By<span class="actor">${safeText(approvedBy)}</span></div>
            <div class="line">Paid By<span class="actor">${safeText(paidBy)}</span></div>
          </div>
        </body>
      </html>
    `;
    const popup = window.open('', '_blank', 'width=800,height=900');
    popup.document.write(content);
    popup.document.close();
    popup.focus();
    popup.print();
  };

  if (loading) {
    return (
      <div className="py-12 flex justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!isFinance) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Payroll</h1>
          <p className="text-gray-600">View your approved and paid salary history</p>
        </div>
        <div className="card">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Net Payable</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Payslip</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {employeeHistory.length === 0 ? (
                  <tr><td colSpan="4" className="px-6 py-8 text-center text-gray-500">No payroll history yet</td></tr>
                ) : employeeHistory.map((line) => (
                  <tr key={line._id}>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{periodLabel(line.payrollRun)}</td>
                    <td className="px-6 py-4 text-right font-semibold">{formatCurrency(line.netPayable)}</td>
                    <td className="px-6 py-4"><span className={lineStatusBadge(line.status)}>{line.status}</span></td>
                    <td className="px-6 py-4 text-right">
                      <button onClick={() => printPayslip({ ...line, employee: user })} className="text-primary-600 hover:text-primary-900">Print</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">HR & Payroll</h1>
          <p className="text-gray-600">Monthly cash payroll, advances, loans, approval, payment, and payslips</p>
        </div>
        <button onClick={loadFinanceData} className="btn-secondary flex items-center justify-center">
          <ArrowPathIcon className="h-4 w-4 mr-2" />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard label="Active Employees" value={summary?.activeEmployees || 0} />
        <StatCard label="Outstanding Advances" value={formatCurrency(summary?.outstandingAdvance)} tone="text-yellow-600" />
        <StatCard label="Outstanding Loans" value={formatCurrency(summary?.outstandingLoan)} tone="text-red-600" />
        <StatCard label="Latest Payroll" value={summary?.latestRun ? periodLabel(summary.latestRun) : 'None'} subtitle={summary?.latestRun?.status || 'No runs yet'} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="card p-5 xl:col-span-2">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Employee Compensation</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Salary</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Advance</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Loan</th>
                  {canSetupCompensation && <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Action</th>}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {compensations.map((row) => (
                  <tr key={row.employee._id}>
                    <td className="px-4 py-3 text-sm">
                      <p className="font-medium text-gray-900">{row.employee.name}</p>
                      <p className="text-xs text-gray-500">{row.employee.employeeId} · {row.employee.profile?.designation || row.employee.role}</p>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold">{formatCurrency(row.baseSalary)}</td>
                    <td className="px-4 py-3 text-right">{formatCurrency(row.outstandingAdvance)}</td>
                    <td className="px-4 py-3 text-right">{formatCurrency(row.outstandingLoan)}</td>
                    {canSetupCompensation && (
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => saveCompensation(row)} className="text-primary-600 hover:text-primary-900">Set Salary</button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card p-5">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Record Advance / Loan</h2>
          {hasRole(['admin', 'accounts']) ? (
            <form onSubmit={createTransaction} className="space-y-3">
              <select value={transactionForm.employee} onChange={(e) => setTransactionForm({ ...transactionForm, employee: e.target.value })} className="input-field" required>
                <option value="">Select employee</option>
                {employees.map((employee) => (
                  <option key={employee._id} value={employee._id}>{employee.name} ({employee.employeeId})</option>
                ))}
              </select>
              <div className="grid grid-cols-2 gap-3">
                <select value={transactionForm.balanceType} onChange={(e) => setTransactionForm({ ...transactionForm, balanceType: e.target.value })} className="input-field">
                  <option value="advance">Advance</option>
                  <option value="loan">Loan</option>
                </select>
                <select value={transactionForm.direction} onChange={(e) => setTransactionForm({ ...transactionForm, direction: e.target.value })} className="input-field">
                  <option value="increase">Give Cash</option>
                  <option value="decrease">Repayment</option>
                </select>
              </div>
              <input type="number" step="0.01" min="0.01" value={transactionForm.amount} onChange={(e) => setTransactionForm({ ...transactionForm, amount: e.target.value })} className="input-field" placeholder="Amount" required />
              <input type="date" value={transactionForm.date} onChange={(e) => setTransactionForm({ ...transactionForm, date: e.target.value })} className="input-field" required />
              <input value={transactionForm.referenceNumber} onChange={(e) => setTransactionForm({ ...transactionForm, referenceNumber: e.target.value })} className="input-field" placeholder="Voucher/reference optional" />
              <textarea value={transactionForm.description} onChange={(e) => setTransactionForm({ ...transactionForm, description: e.target.value })} rows={3} className="input-field" placeholder="Description" required />
              <button type="submit" className="btn-primary w-full">Record Transaction</button>
            </form>
          ) : (
            <p className="text-sm text-gray-500">Only admin/accounts can record advance and loan transactions.</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="card p-5">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Create Payroll Draft</h2>
          {canPreparePayroll ? (
            <form onSubmit={createRun} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <select value={newRun.periodMonth} onChange={(e) => setNewRun({ ...newRun, periodMonth: Number(e.target.value) })} className="input-field">
                  {Array.from({ length: 12 }, (_, index) => index + 1).map((month) => (
                    <option key={month} value={month}>{monthName(month)}</option>
                  ))}
                </select>
                <input type="number" value={newRun.periodYear} onChange={(e) => setNewRun({ ...newRun, periodYear: Number(e.target.value) })} className="input-field" />
              </div>
              <textarea value={newRun.notes} onChange={(e) => setNewRun({ ...newRun, notes: e.target.value })} className="input-field" rows={3} placeholder="Notes optional" />
              <button type="submit" className="btn-primary w-full">Create Draft</button>
            </form>
          ) : (
            <p className="text-sm text-gray-500">Only admin/accounts can prepare payroll drafts.</p>
          )}
        </div>

        <div className="card p-5 xl:col-span-2">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Payroll Runs</h2>
          <div className="space-y-2">
            {runs.length === 0 ? (
              <p className="text-sm text-gray-500">No payroll runs yet.</p>
            ) : runs.map((run) => (
              <button key={run._id} onClick={() => openRun(run._id)} className="w-full text-left border border-gray-200 rounded-md p-3 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{periodLabel(run)}</p>
                    <p className="text-xs text-gray-500">Prepared by {actorLabel(run.preparedBy)}</p>
                  </div>
                  <div className="text-right">
                    <span className={statusBadge(run.status)}>{run.status}</span>
                    <p className="mt-1 font-semibold text-gray-900">{formatCurrency(run.totals?.netPayable)}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {selectedRun && (
        <div className="card">
          <div className="px-6 py-4 border-b border-gray-200 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{periodLabel(selectedRun)}</h2>
              <p className="text-sm text-gray-500">
                Status: <span className={statusBadge(selectedRun.status)}>{selectedRun.status}</span>
                {selectedRun.approvedBy && ` · Approved by ${actorLabel(selectedRun.approvedBy)}`}
                {selectedRun.paidBy && ` · Paid by ${actorLabel(selectedRun.paidBy)}`}
              </p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              {selectedRun.status === 'draft' && canApprovePayroll && (
                <button onClick={approveRun} className="btn-success flex items-center justify-center">
                  <CheckIcon className="h-4 w-4 mr-2" />
                  Approve Payroll
                </button>
              )}
              {selectedRun.status === 'approved' && canPayPayroll && (
                <button onClick={payRun} className="btn-primary flex items-center justify-center">
                  <BanknotesIcon className="h-4 w-4 mr-2" />
                  Mark Cash Paid
                </button>
              )}
            </div>
          </div>

          <div className="p-6 grid grid-cols-1 md:grid-cols-5 gap-4">
            <StatCard label="Gross Salary" value={formatCurrency(selectedRun.totals?.grossSalary)} />
            <StatCard label="Bonus" value={formatCurrency(selectedRun.totals?.bonus)} />
            <StatCard label="Advance Deduction" value={formatCurrency(selectedRun.totals?.advanceDeduction)} />
            <StatCard label="Loan Deduction" value={formatCurrency(selectedRun.totals?.loanDeduction)} />
            <StatCard label="Net Payable" value={formatCurrency(selectedRun.totals?.netPayable)} tone="text-green-600" />
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Base</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Bonus</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Advance</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Loan</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Other</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Net</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {selectedLines.map((line) => {
                  const draft = lineDrafts[line._id] || {};
                  const editable = selectedRun.status === 'draft' && canPreparePayroll;
                  return (
                    <tr key={line._id}>
                      <td className="px-4 py-3 text-sm">
                        <p className="font-medium text-gray-900">{line.employee?.name}</p>
                        <p className="text-xs text-gray-500">{line.employee?.employeeId}</p>
                      </td>
                      {['baseSalary', 'bonus', 'advanceDeduction', 'loanDeduction', 'otherDeduction'].map((field) => (
                        <td key={field} className="px-4 py-3 text-right">
                          {editable ? (
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={draft[field] ?? 0}
                              onChange={(e) => updateLineDraft(line._id, field, e.target.value)}
                              className="input-field text-right min-w-[96px]"
                            />
                          ) : (
                            <span className="font-medium">{formatCurrency(line[field])}</span>
                          )}
                        </td>
                      ))}
                      <td className="px-4 py-3 text-right font-semibold text-gray-900">{formatCurrency(line.netPayable)}</td>
                      <td className="px-4 py-3">
                        {editable ? (
                          <select value={draft.status || 'draft'} onChange={(e) => updateLineDraft(line._id, 'status', e.target.value)} className="input-field min-w-[96px]">
                            <option value="draft">Draft</option>
                            <option value="held">Held</option>
                          </select>
                        ) : (
                          <span className={lineStatusBadge(line.status)}>{line.status}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right space-x-2">
                        {editable && <button onClick={() => saveLine(line._id)} className="text-primary-600 hover:text-primary-900">Save</button>}
                        <button onClick={() => printPayslip(line)} className="text-gray-600 hover:text-gray-900 inline-flex items-center">
                          <PrinterIcon className="h-4 w-4 mr-1" />
                          Payslip
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="card p-5">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Advance / Loan Transactions</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Direction</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {transactions.length === 0 ? (
                <tr><td colSpan="6" className="px-4 py-8 text-center text-gray-500">No advance or loan transactions yet</td></tr>
              ) : transactions.slice(0, 12).map((transaction) => (
                <tr key={transaction._id}>
                  <td className="px-4 py-3 text-sm text-gray-600">{new Date(transaction.date).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-sm">{actorLabel(transaction.employee)}</td>
                  <td className="px-4 py-3 text-sm capitalize">{transaction.balanceType}</td>
                  <td className="px-4 py-3 text-sm capitalize">{transaction.direction}</td>
                  <td className="px-4 py-3 text-right font-semibold">{formatCurrency(transaction.amount)}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{transaction.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Payroll;
